import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import { take } from 'ramda';
import { compact } from '@moda/portal-stanchions';
import { useCountry } from '../../components/CountrySelector';
import { useFavorites } from '../../components/Favorite';
import { VARIANT_CELL_FRAGMENT } from '../../components/Grid/GridCell/VariantCell/VariantCell';
import { TRUNKSHOW_CELL_FRAGMENT } from '../../components/Grid/GridCell/TrunkshowCell/TrunkshowCell';
import {
  VariantsQuery,
  VariantsQuery_variants as Variant,
  VariantsQueryVariables
} from '../../generated/types/VariantsQuery';
import {
  TrunkshowsQuery,
  TrunkshowsQuery_trunkshows_trunkshows_edges_node as Trunkshow,
  TrunkshowsQueryVariables
} from '../../generated/types/TrunkshowsQuery';
import {
  LookVariantsQuery,
  LookVariantsQueryVariables
} from '../../generated/types/LookVariantsQuery';
import { useSegmentAnonymousId } from '../useSegmentAnonymousId';
import { usePreferences } from '../usePreferences';
import { useSkippableQuery } from '../useSkippableQuery';
import { useUser } from '../useUser';
import { getPriceRange } from '../../lib/getPriceRange';
import { RECOMMENDATIONS_QUERY, DESIGNER_AFFINITIES_QUERY } from './recommendations.rest';

export const VARIANTS_QUERY: TypedDocumentNode<VariantsQuery, VariantsQueryVariables> = gql`
  query VariantsQuery($ids: [String!], $countryCode: String) {
    variants(ids: $ids) {
      ...VariantCellFragment
    }
  }
  ${VARIANT_CELL_FRAGMENT}
`;

export const TRUNKSHOWS_QUERY: TypedDocumentNode<TrunkshowsQuery, TrunkshowsQueryVariables> = gql`
  query TrunkshowsQuery(
    $designerIds: [String]
    $vertical: Gender
    $excludedCategories: [String]
    $singleDesignerOnly: Boolean
    $limit: Int
  ) {
    trunkshows(
      input: {
        designer_ids: $designerIds
        gender: $vertical
        excluded_categories: $excludedCategories
        single_designer_only: $singleDesignerOnly
      }
    ) {
      trunkshows(first: $limit) {
        edges {
          node {
            ...TrunkshowCellFragment
            categories
            limited_designer_data {
              id
            }
          }
        }
      }
    }
  }
  ${TRUNKSHOW_CELL_FRAGMENT}
`;

const LOOK_VARIANTS_QUERY: TypedDocumentNode<LookVariantsQuery, LookVariantsQueryVariables> = gql`
  query LookVariantsQuery($variantId: String, $countryCode: String) {
    variant(id: $variantId) {
      productId: product_id
      looks {
        edges {
          node {
            primaryProductId: primary_product_id
            trunkshowId: trunkshow_id
            variants {
              edges {
                node {
                  canSell: can_sell
                  ...VariantCellFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${VARIANT_CELL_FRAGMENT}
`;

const DEFAULT_LIMIT = 40;
const PERCENTAGE_OF_CURRENT_PRICE = 0.3;

export const useVariantsQuery = (variantIds?: string[] | null, limit = DEFAULT_LIMIT) => {
  const {
    country: { alpha3Code }
  } = useCountry();

  return useSkippableQuery(VARIANTS_QUERY, {
    variables: {
      ids: take(limit, compact(variantIds)),
      countryCode: alpha3Code
    },
    skip: !variantIds || variantIds.length === 0,
    ssr: false
  });
};

export const useTrunkshowsQuery = ({
  designerIds,
  vertical,
  excludedCategories,
  singleDesignerOnly,
  limit
}: TrunkshowsQueryVariables) => {
  const { data, loading, error } = useSkippableQuery(TRUNKSHOWS_QUERY, {
    variables: {
      designerIds,
      vertical,
      excludedCategories,
      singleDesignerOnly,
      limit
    },
    skip: (designerIds || []).length === 0,
    ssr: false
  });

  return {
    trunkshows:
      (data?.trunkshows?.trunkshows?.edges
        ?.map(edge => edge?.node)
        .filter(Boolean) as Trunkshow[]) || [],
    loading,
    error
  };
};

export const useRecommendationsQuery = (type: string, id?: string | null) =>
  useSkippableQuery(RECOMMENDATIONS_QUERY, {
    variables: { type, id: id || '', limit: DEFAULT_LIMIT },
    skip: !id,
    ssr: false
  });

export const useVariantsRecommendationsQuery = (
  type: string,
  id?: string | null,
  limit?: number
) => {
  const recommendationsQuery = useRecommendationsQuery(type, id);
  const variantsQuery = useVariantsQuery(
    (recommendationsQuery.data?.recommendations?.variantIds || []).map(String),
    limit
  );

  return {
    variants:
      (variantsQuery.data?.variants?.filter(Boolean) &&
        (variantsQuery.data?.variants?.filter(
          variant => variant?.masterVariant.soldOut === false
        ) as Variant[])) ||
      [],
    loading: recommendationsQuery.loading || variantsQuery.loading,
    error: recommendationsQuery.error || variantsQuery.error
  };
};

export const useRecentlyViewed = () => {
  const segmentId = useSegmentAnonymousId();
  const result = useVariantsRecommendationsQuery('recently_viewed', segmentId);

  if (!segmentId) {
    return {
      variants: [],
      loading: false,
      error: undefined
    };
  }

  return result;
};

export const useFavoritedProducts = () => {
  const { favorites } = useFavorites();
  const variantsQuery = useVariantsQuery(favorites.map(String));

  return {
    variants: (variantsQuery.data?.variants?.filter(Boolean) as Variant[]) || [],
    loading: variantsQuery.loading,
    error: variantsQuery.error
  };
};

export const useSimilarProducts = (variantId?: string, variantPrice?: string | number | null) => {
  const { minLimit, maxLimit } = getPriceRange(PERCENTAGE_OF_CURRENT_PRICE, variantPrice);
  const variantsQuery = useVariantsRecommendationsQuery('visual_similarities', variantId, Infinity);

  const priceFilteredVariants = take(
    DEFAULT_LIMIT,
    variantsQuery.variants.filter(
      variant =>
        variant.masterVariant.prices.currentPrice &&
        Number(variant.masterVariant.prices.currentPrice.price) >= minLimit &&
        Number(variant.masterVariant.prices.currentPrice.price) <= maxLimit
    )
  );

  if (!variantPrice) {
    return {
      variants: variantsQuery.variants,
      loading: variantsQuery.loading,
      error: variantsQuery.error
    };
  }

  return {
    variants: priceFilteredVariants || variantsQuery.variants,
    loading: variantsQuery.loading,
    error: variantsQuery.error
  };
};

export const useLookVariants = (variantId?: string | null) => {
  const { country } = useCountry();

  const { data, loading, error } = useSkippableQuery(LOOK_VARIANTS_QUERY, {
    variables: { variantId: variantId || '', countryCode: country.alpha2Code },
    skip: !variantId,
    ssr: false
  });

  const looks = data?.variant?.looks?.edges || [];
  const trunkshowLooks = looks.filter(look => look?.node?.trunkshowId != null);
  const primaryLooks = looks.filter(
    look => look?.node?.primaryProductId === data?.variant?.productId
  );

  const look = primaryLooks[primaryLooks.length - 1] || trunkshowLooks[trunkshowLooks.length - 1];

  return {
    variants: compact(look?.node?.variants?.edges?.map(edge => edge?.node) || []).filter(
      variant => variant.id !== variantId && variant.canSell
    ),
    loading,
    error
  };
};

export const useTopTrunkshows = () => {
  const { user } = useUser();
  const { vertical } = usePreferences();

  const designerAffinitiesQuery = useQuery(DESIGNER_AFFINITIES_QUERY, {
    variables: { userId: user.id || '0', limit: DEFAULT_LIMIT },
    ssr: false
  });

  const trunkshowsQuery = useTrunkshowsQuery({
    designerIds: (designerAffinitiesQuery.data?.designerAffinities?.top || []).map(String),
    vertical,
    excludedCategories: ['Bridal'],
    singleDesignerOnly: true,
    limit: 16
  });

  return {
    trunkshows: trunkshowsQuery.trunkshows,
    loading: designerAffinitiesQuery.loading || trunkshowsQuery.loading,
    error: designerAffinitiesQuery.error || trunkshowsQuery.error
  };
};
